import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "./BillingStyle.css";

const Billing = () => {
  // ===== STATE MANAGEMENT =====
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  // ===== EFFECTS =====
  useEffect(() => {
    fetchPlans();
  }, []);

  // ===== API CALLS =====
  const fetchPlans = () => {
    // Mock plans - in production, fetch from API
    setPlans([
      {
        id: "basic",
        name: "Basic",
        price: 2.99,
        extractionLimit: 15,
        description: "Perfect for occasional users",
        stripePriceId: "price_basic_mock",
        features: [
          "15 conversions per month",
          "PDF ↔ DOCX conversion",
          "File size up to 10MB",
          "Email support",
          "Basic analytics",
        ],
      },
      {
        id: "premium",
        name: "Premium",
        price: 4.99,
        extractionLimit: 25,
        description: "Ideal for power users",
        stripePriceId: "price_premium_mock",
        popular: true,
        features: [
          "25 conversions per month",
          "PDF ↔ DOCX conversion",
          "Unlimited file size",
          "Priority support 24/7",
          "Advanced analytics",
          "Batch processing",
          "API access",
        ],
      },
    ]);
  };

  // ===== EVENT HANDLERS =====
  const handleSubscribe = async (priceId, planId) => {
    setLoading(true);
    setProcessingPlan(planId);
    try {
      const response = await api.post("/stripe/create-checkout-session", {
        priceId,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.data.sessionId;
    } catch (error) {
      alert("Error creating checkout session. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const getCurrentPlan = () => {
    // Admin users have unlimited plan
    if (user?.role === "admin") return "Unlimited";
    return user?.subscription?.plan || "Free";
  };

  const getRemainingCredits = () => {
    // Admin users have unlimited credits
    if (user?.role === "admin") return "∞";
    return user?.subscription?.extractionCredits || 0;
  };

  const isFreeTrialUsed = () => {
    return user?.freeTrialUsed || false;
  };

  const isPlanActive = (planName) => {
    return getCurrentPlan().toLowerCase() === planName.toLowerCase();
  };

  // ===== RENDER FUNCTIONS =====
  const renderHeader = () => (
    <div className="billing-page-header">
      <div className="billing-save-badge mb-4">
        <i className="fa fa-star me-2"></i>
        Save 20% on Annual Plans
      </div>
      <h1 className="display-3 fw-bold billing-page-title">
        Choose Your Perfect Plan
      </h1>
      <p className="billing-page-subtitle">
        Flexible pricing for teams of all sizes. Upgrade, downgrade, or cancel anytime.
      </p>
    </div>
  );

  const renderPricingCards = () => {
    const allPlans = [
      {
        id: "free",
        name: "Free Trial",
        price: 0,
        period: "One-time",
        description: "Try our service with no commitment",
        icon: "fa-gift",
        iconClass: "billing-icon-free",
        cardClass: "billing-pricing-card-free",
        features: [
          "1 free conversion",
          "PDF ↔ DOCX format",
          "File size up to 5MB",
          "Basic support",
        ],
        buttonText: isFreeTrialUsed() ? "Trial Used" : "Available",
        buttonDisabled: isFreeTrialUsed(),
        isFreeTrial: true,
      },
      ...plans.map((plan) => ({
        ...plan,
        period: "per month",
        icon: plan.id === "basic" ? "fa-rocket" : "fa-star",
        iconClass: plan.id === "basic" ? "billing-icon-basic" : "billing-icon-premium",
        cardClass: plan.popular ? "billing-pricing-card-featured" : "",
        buttonText: isPlanActive(plan.name) ? "Current Plan" : "Subscribe Now",
        buttonDisabled: isPlanActive(plan.name),
      })),
    ];

    return (
      <div className="row g-4 mb-5">
        {allPlans.map((plan, index) => (
          <div key={plan.id} className="col-lg-4 col-md-6">
            <div className={`billing-pricing-card ${plan.cardClass || ""}`}>
              {plan.popular && (
                <div className="billing-popular-badge">
                  <i className="fa fa-bolt me-1"></i> Most Popular
                </div>
              )}

              <div className={`billing-card-plan-icon ${plan.iconClass}`}>
                <i className={`fa ${plan.icon}`}></i>
              </div>

              <h3 className="billing-card-plan-name">{plan.name}</h3>

              <div className="billing-price-wrapper">
                <div className="billing-price-amount">
                  <span className="billing-price-currency">$</span>
                  {plan.price}
                </div>
                <span className="billing-price-period">{plan.period}</span>
              </div>

              <p className="billing-price-description">{plan.description}</p>

              <ul className="billing-features-list">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="billing-feature-item">
                    <div className="billing-feature-icon">
                      <i className="fa fa-check"></i>
                    </div>
                    <span className="billing-feature-text">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.isFreeTrial ? (
                <div className="text-center">
                  {isFreeTrialUsed() ? (
                    <span className="billing-status-badge billing-status-used">
                      <i className="fa fa-check-circle me-2"></i>
                      Trial Used
                    </span>
                  ) : (
                    <span className="billing-status-badge billing-status-available">
                      <i className="fa fa-gift me-2"></i>
                      Available Now
                    </span>
                  )}
                </div>
              ) : (
                <button
                  className={`billing-subscribe-btn ${
                    plan.buttonDisabled ? "billing-subscribe-btn-disabled" : ""
                  }`}
                  onClick={() => handleSubscribe(plan.stripePriceId, plan.id)}
                  disabled={loading || plan.buttonDisabled}
                >
                  {processingPlan === plan.id ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      {plan.buttonDisabled ? (
                        <>
                          <i className="fa fa-check-circle me-2"></i>
                          Current Plan
                        </>
                      ) : (
                        <>
                          <i className="fa fa-shopping-cart me-2"></i>
                          {plan.buttonText}
                        </>
                      )}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCurrentUsage = () => {
    const currentPlan = getCurrentPlan();
    const remainingCredits = getRemainingCredits();
    const trialStatus = isFreeTrialUsed() ? "Used" : "Available";

    return (
      <div className="billing-usage-card">
        <div className="billing-usage-header">
          <div className="billing-usage-icon">
            <i className="fa fa-bar-chart"></i>
          </div>
          <h3 className="billing-usage-title">Your Current Usage</h3>
        </div>

        <div className="billing-usage-grid">
          <div className="billing-usage-item">
            <div className="billing-usage-label">
              <i className="fa fa-star"></i>
              Current Plan
            </div>
            <div className="billing-usage-value">{currentPlan}</div>
          </div>

          <div className="billing-usage-item">
            <div className="billing-usage-label">
              <i className="fa fa-battery-three-quarters"></i>
              Remaining Credits
            </div>
            <div className="billing-usage-value">{remainingCredits}</div>
          </div>

          <div className="billing-usage-item">
            <div className="billing-usage-label">
              <i className="fa fa-gift"></i>
              Free Trial Status
            </div>
            <div className="d-flex align-items-center mt-2">
              <span
                className={`billing-status-badge ${
                  trialStatus === "Available"
                    ? "billing-status-available"
                    : "billing-status-used"
                }`}
              >
                <i
                  className={`fa ${
                    trialStatus === "Available" ? "fa-check-circle" : "fa-times-circle"
                  } me-2`}
                ></i>
                {trialStatus}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFAQ = () => {
    const faqs = [
      {
        question: "Can I change my plan later?",
        answer:
          "Absolutely! You can upgrade, downgrade, or cancel your plan at any time. Changes will be reflected in your next billing cycle.",
      },
      {
        question: "What happens if I run out of credits?",
        answer:
          "When you run out of credits, you can either wait until your next billing cycle or upgrade to a higher plan to get more credits immediately.",
      },
      {
        question: "Do you offer refunds?",
        answer:
          "Yes, we offer a 14-day money-back guarantee. If you're not satisfied with our service, contact our support team for a full refund.",
      },
      {
        question: "Are there any hidden fees?",
        answer:
          "No hidden fees! The price you see is exactly what you'll pay. All features listed in your plan are included.",
      },
    ];

    return (
      <div className="billing-faq-section">
        <div className="billing-faq-header">
          <h2 className="billing-faq-title">Frequently Asked Questions</h2>
          <p className="text-muted">Got questions? We've got answers.</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            {faqs.map((faq, index) => (
              <div key={index} className="billing-faq-item">
                <h5 className="billing-faq-question">
                  <i className="fa fa-question-circle"></i>
                  {faq.question}
                </h5>
                <p className="billing-faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-5">
          <p className="text-muted mb-3">Still have questions?</p>
          <a
            href="mailto:support@example.com"
            className="billing-subscribe-btn billing-subscribe-btn-outline d-inline-block"
            style={{ width: "auto", padding: "0.875rem 2.5rem" }}
          >
            <i className="fa fa-envelope me-2"></i>
            Contact Support
          </a>
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="billing-main-wrapper">
      <div className="container py-5">
        {renderHeader()}
        {renderPricingCards()}
        {renderCurrentUsage()}
        {renderFAQ()}
      </div>
    </div>
  );
};

export default Billing;
